import React, { useEffect, useState, useCallback, useRef } from 'react';
import apps, { getApp } from './apps';
import Window from './Window';
import MenuBar from './MenuBar';
import Dock from './Dock';
import Spotlight from './Spotlight';
import { useOS, WALLPAPERS } from './OSContext';
import { KONAMI_SEQUENCE } from '../../constants/konami';

const desktopApps = apps.filter(a => a.showOnDesktop);

const Desktop = ({ autoOpenAppId }) => {
	const { order, openApp, isMobile, wallpaper } = useOS();
	const [spotlightOpen, setSpotlightOpen] = useState(false);
	const konamiProgress = useRef(0);

	const closeSpotlight = useCallback(() => setSpotlightOpen(false), []);

	// Opens an app immediately on first mount (e.g. "Play Game" on the login
	// screen jumping straight to the Arcade app). Runs once — Desktop only
	// (re)mounts on a real boot->login->desktop transition.
	useEffect(() => {
		if (!autoOpenAppId) return;
		const app = getApp(autoOpenAppId);
		if (app) openApp(app);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleKeyDown = e => {
			const isK = e.key === 'k' || e.key === 'K';
			if ((e.metaKey || e.ctrlKey) && isK) {
				e.preventDefault();
				setSpotlightOpen(open => !open);
			} else if (e.key === 'Escape') {
				setSpotlightOpen(false);
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []);

	// Konami code also auto-launches the Arcade app (the site-wide achievement
	// toast in App.jsx fires independently from its own listener).
	useEffect(() => {
		const handleKeyDown = e => {
			const expected = KONAMI_SEQUENCE[konamiProgress.current];
			const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
			if (key === expected) {
				konamiProgress.current += 1;
				if (konamiProgress.current === KONAMI_SEQUENCE.length) {
					konamiProgress.current = 0;
					openApp(getApp('arcade'));
				}
			} else {
				konamiProgress.current = key === KONAMI_SEQUENCE[0] ? 1 : 0;
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [openApp]);

	const wallpaperCss = (WALLPAPERS.find(w => w.id === wallpaper) || WALLPAPERS[0]).css;

	return (
		<div
			className="fixed inset-0 overflow-hidden font-os select-none"
			style={{ background: wallpaperCss }}
		>
			{/* Desktop icons */}
			{!isMobile && (
				<div className="absolute top-10 left-4 flex flex-col gap-5 z-0">
					{desktopApps.map(app => (
						<button
							key={app.id}
							onClick={() => openApp(app)}
							className="flex flex-col items-center gap-1.5 w-20 px-2 py-2 rounded-lg hover:bg-white/10 focus:bg-white/15 focus:outline-none transition-colors"
						>
							<span className="w-11 h-11 rounded-xl bg-os-surface2/90 border border-white/10 flex items-center justify-center text-lg text-os-text shadow-lg">
								<i className={app.icon} aria-hidden="true" />
							</span>
							<span className="text-[11px] leading-tight text-center text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
								{app.title}
							</span>
						</button>
					))}
				</div>
			)}

			<MenuBar onOpenSpotlight={() => setSpotlightOpen(true)} />

			{order.map(appId => (
				<Window key={appId} appId={appId} isMobile={isMobile} />
			))}

			<Dock />

			<Spotlight open={spotlightOpen} onClose={closeSpotlight} />
		</div>
	);
};

export default Desktop;
